import { Button, notify } from "@flexfreight/ui";
import { useState, type ReactNode } from "react";
import type { IntegrationId } from "./settingsCatalog";

/**
 * "How to connect" for every integration: the steps at the vendor, and every value to copy (scopes, redirect URI,
 * webhook URL) with a Copy button, so an admin can connect or reconnect without help. Values that depend on where
 * the console runs are built from this page's own address (the console proxies /v1 to the API).
 */

export const ZOHO_SCOPES = ["Desk.tickets.READ", "Desk.basic.READ", "Desk.contacts.READ", "Desk.articles.READ", "Desk.tickets.CREATE", "Desk.tickets.UPDATE", "ZohoBooks.fullaccess.all"].join(",");

type Step = { text: ReactNode; copy?: { label: string; value: string } };
type Guide = { intro: string; steps: Step[]; links?: { label: string; href: string }[] };

function origin(): string {
  return typeof window !== "undefined" ? window.location.origin : "https://admin.flexfreight.net";
}

function zohoSteps(): Step[] {
  const redirect = `${origin()}/v1/console/integrations/zoho/callback`;
  return [
    { text: <>Open the Zoho API Console (api-console.zoho.com) with the Zoho account that owns FlexFreight&apos;s Desk and Books.</> },
    {
      text: (
        <>
          <strong>Easiest: Self Client.</strong> Choose Self Client, copy its Client ID and Client secret into this pop-up (Client keys or paste a code), then Generate Code with these scopes, a 10 minute duration, and paste the code here within 3 minutes. A code works once; if you see invalid_code, generate a new one.
        </>
      ),
      copy: { label: "Scopes", value: ZOHO_SCOPES },
    },
    {
      text: (
        <>
          <strong>Or: Connect with Zoho.</strong> Create a Server-based Application, add this as its Authorized Redirect URI, save its Client ID and secret here, then press Connect with Zoho.
        </>
      ),
      copy: { label: "Redirect URI", value: redirect },
    },
    { text: <>Pick the same data center as the Zoho account (zoho.com is the United States). Changing the client or data center needs a new connection.</> },
    { text: <>The test runs on its own after connecting. Then set up Zoho Desk (organization ID and departments) and Zoho Books (organization ID).</> },
  ];
}

const GUIDES: Record<IntegrationId, () => Guide> = {
  zoho: () => ({ intro: "One Zoho sign-in that Zoho Desk and Zoho Books share.", steps: zohoSteps(), links: [{ label: "Zoho API Console", href: "https://api-console.zoho.com/" }] }),
  "zoho-desk": () => ({
    intro: "Connect Zoho first (Zoho connection, first in this window), then point this at FlexFreight's Desk.",
    steps: [
      ...zohoSteps().slice(0, 2),
      { text: <>Find the organization ID in Zoho Desk: Setup, Developer Space, API. Enter it under Change settings.</> },
      { text: <>Pick only FlexFreight departments (Empire Telecom and Elink departments are refused). Press Test connection, then turn Sync on.</> },
      { text: <>Two-way sync (creating and updating tickets in Zoho Desk) is a separate switch and needs the CREATE and UPDATE scopes above.</> },
    ],
    links: [{ label: "Zoho API Console", href: "https://api-console.zoho.com/" }],
  }),
  "zoho-books": () => ({
    intro: "Connect Zoho first (Zoho connection, first in this window), then point this at FlexFreight's Books organization.",
    steps: [
      ...zohoSteps().slice(0, 2),
      { text: <>Find the organization ID in Zoho Books: Settings, Organization Profile. Enter it and press Test connection.</> },
      { text: <>Turn on auto-invoicing once the test passes. Every booking then creates a Net 15 invoice and emails it to the customer.</> },
    ],
    links: [{ label: "Zoho API Console", href: "https://api-console.zoho.com/" }],
  }),
  m365: () => ({
    intro: "Read and send mail for FlexFreight mailboxes only, through an Entra ID app.",
    steps: [
      { text: <>In Microsoft Entra admin center, App registrations, New registration (single tenant). Copy the Directory (tenant) ID and Application (client) ID into Change settings.</> },
      { text: <>Certificates &amp; secrets, New client secret. Paste the secret&apos;s Value (not its ID).</> },
      { text: <>API permissions, Add, Microsoft Graph, Application permissions: add these two, then Grant admin consent.</>, copy: { label: "Graph permissions", value: "Mail.Read, Mail.Send" } },
      {
        text: <>Limit the app to FlexFreight mailboxes in Exchange Online PowerShell (replace the client ID and members), so Empire mailboxes stay out.</>,
        copy: {
          label: "PowerShell",
          value:
            'New-DistributionGroup -Name "FlexFreight Console Mailboxes" -Type Security -Members support@flexfreight.net,sales@flexfreight.net\nNew-ApplicationAccessPolicy -AppId <client id> -PolicyScopeGroupId "FlexFreight Console Mailboxes" -AccessRight RestrictAccess -Description "FlexFreight console"',
        },
      },
      { text: <>List the FlexFreight mailboxes, choose who can see each, press Test connection. Outbound email is a separate switch only an owner can turn on.</> },
    ],
    links: [{ label: "Entra admin center", href: "https://entra.microsoft.com/" }],
  }),
  resend: () => ({
    intro: "Sends sign-in codes, staff invites and (when chosen) customer emails.",
    steps: [
      { text: <>In Resend, Domains: add flexfreight.net and add the DNS records it shows at your DNS provider. Wait until the domain says Verified.</> },
      { text: <>API Keys, Create API key with Sending access. It starts with re_. Paste it here.</> },
      { text: <>Set the From address on the verified domain.</>, copy: { label: "From", value: "FlexFreight <no-reply@flexfreight.net>" } },
      { text: <>Save, then Send test to me. Turn on Use Resend for customer emails so invites, resets and notices go out too.</> },
    ],
    links: [{ label: "Resend dashboard", href: "https://resend.com/domains" }],
  }),
  twilio: () => ({
    intro: "Texts sign-in codes to staff phones.",
    steps: [
      { text: <>In the Twilio Console home page, copy the Account SID (starts with AC) and Auth token.</> },
      { text: <>Buy or pick a number that can send SMS (enter it as +15551234567), or a Messaging Service SID (starts with MG). Register it for US A2P 10DLC if you text US numbers.</> },
      { text: <>Keep Allowed country codes to the countries your staff are in (1 is the US and Canada). Save, then send a test to your phone.</> },
    ],
    links: [{ label: "Twilio Console", href: "https://console.twilio.com/" }],
  }),
  anthropic: () => ({
    intro: "Turns Flex AI from demonstration mode into live answers.",
    steps: [
      { text: <>In the Anthropic Console, API Keys, Create Key. It starts with sk-ant-.</> },
      { text: <>Paste it and press Save key. Flex AI uses read-only tools under each staff member&apos;s own permissions.</> },
    ],
    links: [{ label: "Anthropic Console", href: "https://console.anthropic.com/settings/keys" }],
  }),
  eagleload: () => ({
    intro: "Live LTL rates from EagleLoad's carrier network, and booking when you choose it.",
    steps: [
      { text: <>In EagleLoad, Settings, create a personal API key (the company key can&apos;t create quotes). Paste it with your username.</> },
      { text: <>For tracking updates, add this webhook in EagleLoad and paste its signing secret here (16 or more characters).</>, copy: { label: "Webhook URL", value: `${origin()}/v1/webhooks/eagleload` } },
      { text: <>Press Test connection (one sample quote, nothing booked), then turn on Quote through EagleLoad.</> },
      { text: <>Booking starts Off (quote only). Test sends no pickups; Live sends real pickup requests and asks you to type GO LIVE EAGLELOAD.</> },
    ],
    links: [{ label: "EagleLoad", href: "https://app.eagleload.com/" }],
  }),
  "google-maps": () => ({
    intro: "Street address suggestions in quotes and bookings.",
    steps: [
      { text: <>In Google Cloud Console, pick or create a project with billing, then enable this API.</>, copy: { label: "API to enable", value: "Places API (New)" } },
      { text: <>Credentials, Create credentials, API key. Restrict it to Places API (New). Don&apos;t add website (referrer) restrictions: the FlexFreight server makes the calls.</> },
      { text: <>Paste the key (starts with AIza), press Test, then turn Address suggestions on.</> },
    ],
    links: [{ label: "Google Cloud credentials", href: "https://console.cloud.google.com/apis/credentials" }],
  }),
  "signin-policy": () => ({
    intro: "Owners and admins must use a second step (authenticator app or text code). No keys needed.",
    steps: [
      { text: <>Leave it on. Only an owner can turn it off, and only while signed in with an authenticator or text code.</> },
      { text: <>Text codes need Twilio connected. Email codes need Resend.</> },
    ],
  }),
};

function CopyRow({ label, value }: { label: string; value: string }) {
  const copy = () =>
    void navigator.clipboard
      ?.writeText(value)
      .then(() => notify.success(`${label} copied`))
      .catch(() => notify.error("Couldn't copy", "Select the text and copy it instead."));
  return (
    <div className="ffa-guide__copy">
      <span className="ffa-guide__copylabel">{label}</span>
      <code className="ffa-guide__value">{value}</code>
      <Button size="sm" onClick={copy}>Copy {label.toLowerCase()}</Button>
    </div>
  );
}

/** Collapsible "How to connect" panel, open by default when the integration isn't connected yet. */
export function IntegrationGuide({ id, defaultOpen }: { id: IntegrationId; defaultOpen: boolean }) {
  const g = GUIDES[id]();
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className="ffa-guide" aria-label="How to connect">
      <button type="button" className="ffa-guide__head" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        <span>How to connect</span>
        <span className="ffa-guide__intro">{g.intro}</span>
        <svg className="ffa-guide__chev" viewBox="0 0 16 16" aria-hidden="true">
          <path d="M4 6l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      </button>
      {open ? (
        <div className="ffa-guide__body">
          <ol className="ffa-guide__steps">
            {g.steps.map((s, i) => (
              <li key={i}>
                <p>{s.text}</p>
                {s.copy ? <CopyRow label={s.copy.label} value={s.copy.value} /> : null}
              </li>
            ))}
          </ol>
          {g.links?.length ? (
            <p className="ffa-guide__links">
              {g.links.map((l) => (
                <a key={l.href} href={l.href} target="_blank" rel="noreferrer noopener">{l.label}</a>
              ))}
            </p>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
